/* eslint-disable no-plusplus */
import {
  AssignerPrograms,
  CaseDetails,
  CaseResultDetails,
  EconsultUserRoles,
  NoteActionTypes,
  NoteStatusCodes,
  NoteTypes,
  UserDTO
} from '@oh-vcp/components-common';
import CaseService from './caseService';

const isEconsultDelegateOfConsultantOnlyInternal = (user: UserDTO) => {
  const { services } = user;
  const { roles } = user;
  return (
    services &&
    services.econsult &&
    roles?.econCaseDelegateInherit &&
    !roles?.econCaseConsultOnAsDelegate &&
    !roles?.econManagedCaseConsultOnAsDelegate
  );
};

const EconsultUserService = {
  hasTriage: (caseResults: CaseResultDetails[]) => {
    const triageCase = caseResults.find((c) => c.userRole === NoteTypes.triageAdmin);
    if (triageCase) return true;
    return false;
  },
  hasTriageAssigner: (caseResults: CaseResultDetails[], assignerPrograms: AssignerPrograms) => {
    const triageCase = caseResults.find((c) => c.userRole === NoteTypes.triageAdmin);
    if (!triageCase) {
      return false;
    }
    const isInProgram = (programId: number) => {
      const program = assignerPrograms?.triageAssigner?.find((id: number) => id === programId);
      if (program) return true;
      return false;
    };
    const triageAssignerCase = caseResults.find((c) => isInProgram(c.programId));
    if (triageAssignerCase) return true;
    return false;
  },
  hasRefs: (caseResults: CaseResultDetails[]) => {
    const refCases = caseResults.find(
      (c) =>
        c.userRole === EconsultUserRoles.referrer ||
        c.userRole === EconsultUserRoles.referrerDelegate
    );
    if (refCases) return true;
    return false;
  },
  hasCons: (caseResults: CaseResultDetails[]) => {
    const consultCases = caseResults.find(
      (c) =>
        c.userRole === EconsultUserRoles.consultant ||
        c.userRole === EconsultUserRoles.consultantDelegate
    );
    if (consultCases) return true;
    return false;
  },
  isEconsultReferrer: (user: UserDTO) => {
    const { services } = user;
    const { roles } = user;
    return (
      services &&
      services.econsult &&
      (roles?.econManagedCaseCreateOn || roles?.econCaseCreate) &&
      !isEconsultDelegateOfConsultantOnlyInternal(user)
    );
  },
  isEconsultConsultant: (user: UserDTO) => {
    const { services } = user;
    const { roles } = user;
    return (
      services &&
      services.econsult &&
      (roles?.econManagedCaseConsultOn || roles?.econCaseConsultOn) &&
      !isEconsultDelegateOfConsultantOnlyInternal(user)
    );
  },
  isEconsultDelegate: (user: UserDTO) => {
    return user && user.roles && user.roles.econCaseDelegateInherit === true;
  },
  isReferrerResolveCompletedConsultAction: (caseDetails: CaseDetails, userRole: string) => {
    if (
      !caseDetails ||
      (userRole !== EconsultUserRoles.referrer && userRole !== EconsultUserRoles.referrerDelegate)
    )
      return false;
    const lastActiveNote = CaseService.getLastActiveNote(caseDetails);
    if (!lastActiveNote || caseDetails.statusCd === NoteStatusCodes.closed) {
      return false;
    }
    if (
      lastActiveNote.actionTypeCd === NoteActionTypes.writeConsult ||
      lastActiveNote.actionTypeCd === NoteActionTypes.recomplete
    ) {
      return true;
    }
    return (
      lastActiveNote.actionTypeCd === NoteActionTypes.decline &&
      CaseService.containsConsultProvidedNote(caseDetails)
    );
  },
  isCurrentUserCaseDepartmentalAssigner: (
    userRole: string | undefined,
    assignerPrograms: AssignerPrograms | undefined,
    programId: number
  ) => {
    if (
      userRole !== NoteTypes.triageAdmin ||
      !assignerPrograms ||
      !assignerPrograms.departmentalAssigner
    ) {
      return false;
    }
    const program = assignerPrograms.departmentalAssigner.filter((a) => a === programId);
    return Boolean(program && program.length > 0);
  },
  isCurrentUserCaseTriageAssigner: (
    userRole: string | undefined,
    assignerPrograms: AssignerPrograms | undefined,
    programId: number
  ) => {
    if (
      userRole !== NoteTypes.triageAdmin ||
      !assignerPrograms ||
      !assignerPrograms.triageAssigner
    ) {
      return false;
    }

    const program = assignerPrograms.triageAssigner.filter((a) => a === programId);
    return Boolean(program && program.length > 0);
  },
  isTriageRedirectConsultAction: (
    caseDetails: CaseDetails | undefined,
    userRole: string | undefined
  ) => {
    if (!caseDetails) return false;
    const lastActiveNote = CaseService.getLastActiveNote(caseDetails);
    if (userRole !== NoteTypes.triageAdmin || !lastActiveNote) {
      return false;
    }
    if (CaseService.containsConsultProvidedNote(caseDetails)) {
      return false;
    }
    if (caseDetails.statusCd !== NoteStatusCodes.active) {
      return false;
    }
    if (
      (NoteTypes.consultant === lastActiveNote.noteTypeCd &&
        NoteActionTypes.askForMoreInfo === lastActiveNote.actionTypeCd) ||
      (NoteTypes.referrer === lastActiveNote.noteTypeCd &&
        NoteActionTypes.reply === lastActiveNote.actionTypeCd)
    ) {
      return false;
    }
    if (
      NoteTypes.consultant === lastActiveNote.noteTypeCd &&
      NoteActionTypes.decline === lastActiveNote.actionTypeCd &&
      (!lastActiveNote.parameter1 ||
        lastActiveNote.parameter1 === 'Other' ||
        lastActiveNote.parameter1 === 'Incorrect Specialty' ||
        lastActiveNote.parameter1 === 'Not Available')
    ) {
      return false;
    }
    if (
      NoteTypes.triageAdmin === lastActiveNote.noteTypeCd &&
      NoteActionTypes.assign === lastActiveNote.actionTypeCd
    ) {
      return false;
    }
    return true;
  },
  isDepartmentalOrTriageAssignerAction: (
    caseDetails: CaseDetails | undefined,
    userRole: string | undefined,
    assignerPrograms: AssignerPrograms | undefined
  ) => {
    if (!caseDetails) return false;
    const { participant } = caseDetails;
    const isAssigned = participant && participant.consultant;
    const lastSpecialistNote = CaseService.getLastNoteWithRole(caseDetails, NoteTypes.consultant);
    const containsUnassignNote = CaseService.containsUnassignNote(caseDetails);
    return (
      (EconsultUserService.isCurrentUserCaseDepartmentalAssigner(
        userRole,
        assignerPrograms,
        caseDetails.participant?.program?.programId
      ) ||
        EconsultUserService.isCurrentUserCaseTriageAssigner(
          userRole,
          assignerPrograms,
          caseDetails.participant?.program?.programId
        )) &&
      participant?.triageStatusCd === NoteStatusCodes.active &&
      !isAssigned &&
      !containsUnassignNote &&
      (!lastSpecialistNote || lastSpecialistNote.length === 0)
    );
  }
};

export default EconsultUserService;
